import { Loader2 } from "lucide-react";
import { Navigate, Route, Routes } from "react-router-dom";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import { Layout } from "./components/Layout";
import { LoginPage } from "./pages/LoginPage";
import { DashboardPage } from "./pages/DashboardPage";
import { PhonesPage } from "./pages/PhonesPage";
import { PhoneStreamPage } from "./pages/PhoneStreamPage";
import { AccountsPage } from "./pages/AccountsPage";
import { AccountDetailPage } from "./pages/AccountDetailPage";
import { ScriptsPage } from "./pages/ScriptsPage";
import { ScriptEditorPage } from "./pages/ScriptEditorPage";
import { JobsPage } from "./pages/JobsPage";
import { JobDetailPage } from "./pages/JobDetailPage";
import { DocumentationPage } from "./pages/DocumentationPage";
import { FailuresPage } from "./pages/FailuresPage";
import { ShareCenterPage } from "./pages/ShareCenterPage";
import { UsersPage } from "./pages/UsersPage";

function ProtectedRoute({ children }: { children: JSX.Element }): JSX.Element {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ink-900">
        <Loader2 className="size-8 text-velvet-500 animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Garde une route admin-only : un employee qui tente d'y accéder est
// redirigé vers la home. Garde côté navigation, le backend bloque aussi
// au niveau API (defense in depth).
function AdminRoute({ children }: { children: JSX.Element }): JSX.Element {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ink-900">
        <Loader2 className="size-8 text-velvet-500 animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function PublicOnlyRoute({ children }: { children: JSX.Element }): JSX.Element {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ink-900">
        <Loader2 className="size-8 text-velvet-500 animate-spin" />
      </div>
    );
  }
  if (user) return <Navigate to="/" replace />;
  return children;
}

export function App(): JSX.Element {
  return (
    <AuthProvider>
      <Routes>
        <Route
          path="/login"
          element={
            <PublicOnlyRoute>
              <LoginPage />
            </PublicOnlyRoute>
          }
        />
        <Route
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route path="/" element={<DashboardPage />} />
          <Route path="/phones" element={<PhonesPage />} />
          <Route path="/phones/:id/stream" element={<PhoneStreamPage />} />
          <Route path="/scripts" element={<AdminRoute><ScriptsPage /></AdminRoute>} />
          <Route path="/scripts/:id" element={<AdminRoute><ScriptEditorPage /></AdminRoute>} />
          <Route path="/jobs" element={<JobsPage />} />
          <Route path="/jobs/:id" element={<JobDetailPage />} />
          <Route path="/accounts" element={<AccountsPage />} />
          <Route path="/accounts/:id" element={<AccountDetailPage />} />
          <Route path="/failures" element={<FailuresPage />} />
          <Route path="/share" element={<ShareCenterPage />} />
          <Route path="/users" element={<AdminRoute><UsersPage /></AdminRoute>} />
          <Route path="/documentation" element={<DocumentationPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthProvider>
  );
}
